function isProgramItemInvalid(item) {
  return (
    !item.supplier?.label ||
    !item.product?.label ||
    item.productType === null ||
    item.productType === "" ||
    isProductCostMissing(item)
  );
}

function getNumericRates(item) {
  const values = item.productCost?.value || {};
  return ["default", "single", "twin", "triple", "quad"]
    .map((key) => values[key])
    .filter((value) => value != null && value !== "")
    .map((value) => Number(value))
    .filter((value) => !Number.isNaN(value));
}

export function isProductCostMissing(item) {
  if (!item) return false;

  const hasRow = Boolean(item.product?.label || item.supplier?.label);
  if (!hasRow) return false;

  const rates = getNumericRates(item);
  if (!rates.length) return true;

  const isLinkedProduct = item.product?.value != null;
  const hasRateType = Boolean(item.productCost?.type);
  if (isLinkedProduct && !hasRateType && rates.every((rate) => rate === 0)) return true;

  return false;
}

function getProgramItemIssues(item, rowLabel) {
  const issues = [];

  if (!item.supplier?.label) {
    issues.push({ section: "program", anchor: "quote-section-program", message: `${rowLabel}: Supplier is required` });
  }
  if (!item.product?.label) {
    issues.push({ section: "program", anchor: "quote-section-program", message: `${rowLabel}: Product is required` });
  }
  if (item.productType === null || item.productType === "") {
    issues.push({ section: "program", anchor: "quote-section-program", message: `${rowLabel}: Product type is required` });
  }
  if (isProductCostMissing(item)) {
    issues.push({
      section: "program",
      anchor: "quote-section-program",
      message: `${rowLabel}: Cost/rate is missing`,
    });
  }

  return issues;
}

function getLogisticsItemIssues(cost, rowLabel) {
  const issues = [];

  if (!cost.supplier?.label) {
    issues.push({ section: "logistics", anchor: "quote-section-logistics", message: `${rowLabel}: Supplier is required` });
  }
  if (!cost.product?.label) {
    issues.push({ section: "logistics", anchor: "quote-section-logistics", message: `${rowLabel}: Product is required` });
  }
  if (cost.productType == null || cost.productType === "") {
    issues.push({ section: "logistics", anchor: "quote-section-logistics", message: `${rowLabel}: Product type is required` });
  }
  if (isProductCostMissing(cost)) {
    issues.push({
      section: "logistics",
      anchor: "quote-section-logistics",
      message: `${rowLabel}: Cost/rate is missing`,
    });
  }

  return issues;
}

export function getQuoteValidationIssues(data) {
  if (!data) {
    return [{ section: "general", anchor: "quote-section-general", message: "Quote data is missing" }];
  }

  const issues = [];

  if (data.reference === null) {
    issues.push({ section: "general", anchor: "quote-section-general", message: "Reference is required (General section)" });
  }
  if (data.client?.id === null || data.client?.id === undefined) {
    issues.push({ section: "general", anchor: "quote-section-general", message: "Client is required (General section)" });
  }
  if (data.cityStart === null) {
    issues.push({
      section: "general",
      anchor: "quote-section-general",
      message: "Start city is required (Tour Information)",
    });
  }
  if (data.cityEnd === null) {
    issues.push({
      section: "general",
      anchor: "quote-section-general",
      message: "End city is required (Tour Information)",
    });
  }
  if (data.nights === null) {
    issues.push({
      section: "general",
      anchor: "quote-section-general",
      message: "Number of nights is required (Tour Information)",
    });
  }
  if (data.title === null) {
    issues.push({ section: "general", anchor: "quote-section-general", message: "Title is required (General section)" });
  }
  if (data.period?.startYear === null) {
    issues.push({
      section: "general",
      anchor: "quote-section-general",
      message: "Arrival date is required (Tour Information)",
    });
  }
  if (data.period?.endYear === null) {
    issues.push({
      section: "general",
      anchor: "quote-section-general",
      message: "Departure date is required (Tour Information)",
    });
  }
  if (!data.peopleRanges?.length) {
    issues.push({
      section: "total-costs",
      anchor: "quote-section-total-costs",
      message: "At least one PAX range is required (Total Costs section)",
    });
  }
  if (data.margin?.percentFixed === null || data.margin?.percentFixed?.toString() === "") {
    issues.push({
      section: "margin",
      anchor: "quote-section-margin",
      message: "Fixed margin percentage is required (Margin section)",
    });
  }

  Object.values(data.margin?.percentDynamic || {}).forEach((value, index) => {
    if (value === null || value === "") {
      issues.push({
        section: "margin",
        anchor: "quote-section-margin",
        message: `Margin percentage is required for PAX range ${index + 1} (Margin section)`,
      });
    }
  });

  (data.costs || []).forEach((day, dayIndex) => {
    (day.items || []).forEach((item, itemIndex) => {
      if (!isProgramItemInvalid(item)) return;
      issues.push(...getProgramItemIssues(item, `Program Day ${dayIndex + 1}, row ${itemIndex + 1}`));
    });
  });

  (data.logisticsCosts || []).forEach((cost, index) => {
    if (
      cost.supplier?.label &&
      cost.product?.label &&
      cost.productType != null &&
      cost.productType !== "" &&
      !isProductCostMissing(cost)
    ) {
      return;
    }
    issues.push(...getLogisticsItemIssues(cost, `Logistics row ${index + 1}`));
  });

  return issues;
}

export function isQuoteValid(data) {
  return getQuoteValidationIssues(data).length === 0;
}

export function formatQuoteValidationMessage(issues, maxItems = 6) {
  if (!issues?.length) return "";

  const lines = issues.slice(0, maxItems).map((issue) => `• ${issue.message}`);
  if (issues.length > maxItems) {
    lines.push(`• ...and ${issues.length - maxItems} more issue(s). See the list at the top of the form.`);
  }

  return `Please fix the following before saving:\n${lines.join("\n")}`;
}
